import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, Mail, Phone, Lock, Eye, EyeOff, Calendar, ArrowRight, AlertCircle, ImagePlus } from "lucide-react";
import { AuthShell, FieldGroup } from "@/pages/Login";
import { useAuth } from "@/context/AuthContext";
import {
  PHONE_COUNTRIES,
  buildWhatsAppE164,
  countryByIso,
  nationalPhoneDigits,
  sanitizeNationalPhone,
} from "@/lib/phoneCountries";

const ROLES = [
  { id: "user",       backend: "user",       label: "Quiero recibir diagnósticos y acompañamiento", desc: "Accederé como Usuario a estudios, consultas y marketplace." },
  { id: "consultor",  backend: "consultant", label: "Quiero trabajar como consultor", desc: "Mi solicitud y fotografía oficial quedarán pendientes de aprobación." },
  { id: "creador",    backend: "creator",    label: "Quiero vender productos en Marketplace", desc: "Mi solicitud quedará pendiente de aprobación. Navego como Usuario mientras tanto." },
];

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPwd, setShowPwd] = useState(false);
  const [roleSel, setRoleSel] = useState("user");
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [countryIso, setCountryIso] = useState("CO");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [dobDay, setDobDay] = useState("");
  const [dobMonth, setDobMonth] = useState("");
  const [dobYear, setDobYear] = useState("");
  const [consultantPhotoData, setConsultantPhotoData] = useState("");
  const [consultantPhotoPreview, setConsultantPhotoPreview] = useState("");

  const consultantSelected = roleSel === "consultor";

  const selectConsultantPhoto = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setError("La fotografía frontal debe ser JPG, PNG o WebP y pesar máximo 5 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setConsultantPhotoData(String(reader.result || ""));
      setConsultantPhotoPreview(String(reader.result || ""));
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const selectedCountry = countryByIso(countryIso);
  const nationalNumber = nationalPhoneDigits(phone);
  const whatsapp = buildWhatsAppE164(countryIso, phone);
  const currentYear = new Date().getFullYear();
  const dobYears = Array.from({ length: currentYear - 1899 }, (_, index) => currentYear - index);
  const dobMonths = [
    ["01", "Enero"], ["02", "Febrero"], ["03", "Marzo"], ["04", "Abril"],
    ["05", "Mayo"], ["06", "Junio"], ["07", "Julio"], ["08", "Agosto"],
    ["09", "Septiembre"], ["10", "Octubre"], ["11", "Noviembre"], ["12", "Diciembre"],
  ];
  const dobDays = Array.from({ length: 31 }, (_, index) => String(index + 1).padStart(2, "0"));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (nationalNumber.length < 6 || whatsapp.replace(/\D/g, "").length > 15) {
      setError("Ingresa un número de WhatsApp válido para el país seleccionado.");
      return;
    }
    let dateOfBirth = null;
    const hasAnyBirthPart = Boolean(dobDay || dobMonth || dobYear);
    if (hasAnyBirthPart) {
      if (!dobDay || !dobMonth || !dobYear) {
        setError("Completa día, mes y año de nacimiento, o deja los tres campos vacíos.");
        return;
      }
      const candidate = `${dobYear}-${dobMonth}-${dobDay}`;
      const parsed = new Date(`${candidate}T00:00:00Z`);
      const validDate = !Number.isNaN(parsed.getTime())
        && parsed.getUTCFullYear() === Number(dobYear)
        && parsed.getUTCMonth() + 1 === Number(dobMonth)
        && parsed.getUTCDate() === Number(dobDay);
      const today = new Date();
      const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
      if (!validDate || parsed > todayUtc) {
        setError("Selecciona una fecha de nacimiento válida.");
        return;
      }
      dateOfBirth = candidate;
    }
    if (!terms || !privacy) {
      setError("Debes aceptar términos y política de privacidad.");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    const selectedRole = ROLES.find((r) => r.id === roleSel);
    const intent = selectedRole?.backend || "user";
    if (consultantSelected && !consultantPhotoData) {
      setError("Debes subir una fotografía frontal oficial para solicitar el rol Consultor.");
      return;
    }
    setLoading(true);
    const res = await register({ name, email, password, phone: whatsapp, whatsapp_opt_in: true, date_of_birth: dateOfBirth, intent, consultant_photo_data: consultantPhotoData || null });
    setLoading(false);
    if (!res.ok) {
      setError(res.error || "No se pudo crear la cuenta");
      return;
    }
    const destination = consultantSelected
      ? "/post-register/consultor"
      : roleSel === "creador"
        ? "/post-register/creador"
        : "/post-register/cliente";
    if (res.verificationRequired) {
      const activation = {
        activationToken: res.activationToken,
        phoneMasked: res.phoneMasked,
        otpSent: Boolean(res.otpSent),
        deliveryStatus: res.deliveryStatus || (res.otpSent ? "sent" : "pending_retry"),
        expiresInSeconds: res.expiresInSeconds || 0,
        message: res.message || "",
        destination,
      };
      window.sessionStorage.setItem("kd9-whatsapp-activation", JSON.stringify(activation));
      navigate("/verificar-whatsapp", { replace: true, state: activation });
      return;
    }
    navigate(destination);
  };

  return (
    <AuthShell side="left">
      <div className="w-full max-w-lg kd9-fade-up py-8" data-testid="register-form-container">
        <h1 className="font-cinzel kd9-gold-text text-3xl md:text-4xl tracking-[0.06em] text-center mb-2">
          CREAR CUENTA EN KD9
        </h1>
        <p
          className="font-cormorant italic text-lg text-center mb-8"
          style={{ color: "var(--kd9-text-soft)" }}
        >
          Accede a estudios, consultas, academia y tienda energética.
        </p>

        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div
              className="flex items-start gap-2 px-4 py-3 rounded-lg border"
              style={{
                borderColor: "rgba(122,14,23,0.5)",
                background: "rgba(122,14,23,0.12)",
                color: "#FCA5A5",
              }}
              data-testid="register-error"
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span className="text-sm font-mont">{error}</span>
            </div>
          )}
          <FieldGroup label="Nombre completo" icon={User}>
            <input className="kd9-input" placeholder="Tu nombre" value={name} onChange={(e) => setName(e.target.value)} required data-testid="register-name-input" />
          </FieldGroup>
          <FieldGroup label="Correo electrónico" icon={Mail}>
            <input type="email" autoComplete="email" className="kd9-input" placeholder="tucorreo@kd9.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input" />
          </FieldGroup>
          <p className="text-xs font-mont -mt-2" style={{ color: "var(--kd9-text-soft)" }} data-testid="register-email-policy-note">
            Usa un correo personal o empresarial permanente. KD9 no admite correos temporales o desechables.
          </p>
          <FieldGroup label="WhatsApp (obligatorio)" icon={Phone}>
            <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] gap-2">
              <select
                className="kd9-input pl-10 pr-2 min-w-0"
                value={countryIso}
                onChange={(e) => setCountryIso(e.target.value)}
                aria-label="País de WhatsApp"
                required
                data-testid="register-whatsapp-country"
              >
                {PHONE_COUNTRIES.map((country) => (
                  <option key={country.iso} value={country.iso}>
                    {country.name} ({country.dial})
                  </option>
                ))}
              </select>
              <div className="relative min-w-0">
                <span
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-mont pointer-events-none"
                  style={{ color: "var(--kd9-gold)" }}
                  aria-hidden="true"
                >
                  {selectedCountry.dial}
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  className="kd9-input min-w-0"
                  style={{ paddingLeft: `${Math.max(3.25, selectedCountry.dial.length * 0.72 + 1.7)}rem` }}
                  placeholder="300 000 0000"
                  value={phone}
                  onChange={(e) => setPhone(sanitizeNationalPhone(e.target.value))}
                  required
                  aria-describedby="register-whatsapp-help"
                  data-testid="register-phone-input"
                />
              </div>
            </div>
          </FieldGroup>
          <p id="register-whatsapp-help" className="text-xs font-mont -mt-2" style={{ color: "var(--kd9-text-soft)" }}>
            Selecciona el país; KD9 agrega automáticamente el indicativo. Escribe únicamente el número nacional.
          </p>
          <p className="text-xs font-mont -mt-2" style={{ color: "var(--kd9-text-soft)" }} data-testid="register-whatsapp-required-note">
            Al crear la cuenta, KD9 enviará un código de 6 dígitos a este WhatsApp. Debes validarlo antes del primer ingreso.
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            <FieldGroup label="Contraseña" icon={Lock}>
              <input
                type={showPwd ? "text" : "password"}
                className="kd9-input pr-12"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPwd(!showPwd)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
                aria-label="Mostrar contraseña"
              >
                {showPwd ? (
                  <EyeOff size={16} style={{ color: "var(--kd9-text-soft)" }} />
                ) : (
                  <Eye size={16} style={{ color: "var(--kd9-text-soft)" }} />
                )}
              </button>
            </FieldGroup>
            <FieldGroup label="Confirmar contraseña" icon={Lock}>
              <input
                type={showPwd ? "text" : "password"}
                className="kd9-input"
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                data-testid="register-confirm-input"
              />
            </FieldGroup>
          </div>

          <FieldGroup label="Fecha de nacimiento (opcional)" icon={Calendar}>
            <div className="grid grid-cols-[0.8fr_1.25fr_1fr] gap-2 pl-10" data-testid="register-dob-input">
              <select
                className="kd9-input !pl-4 !pr-2 min-w-0"
                value={dobDay}
                onChange={(e) => setDobDay(e.target.value)}
                aria-label="Día de nacimiento"
                data-testid="register-dob-day"
              >
                <option value="">Día</option>
                {dobDays.map((day) => <option key={day} value={day}>{Number(day)}</option>)}
              </select>
              <select
                className="kd9-input !pl-4 !pr-2 min-w-0"
                value={dobMonth}
                onChange={(e) => setDobMonth(e.target.value)}
                aria-label="Mes de nacimiento"
                data-testid="register-dob-month"
              >
                <option value="">Mes</option>
                {dobMonths.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <select
                className="kd9-input !pl-4 !pr-2 min-w-0"
                value={dobYear}
                onChange={(e) => setDobYear(e.target.value)}
                aria-label="Año de nacimiento"
                data-testid="register-dob-year"
              >
                <option value="">Año</option>
                {dobYears.map((year) => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>
          </FieldGroup>

          <div className="pt-2">
            <div
              className="font-mont text-[11px] tracking-[0.25em] uppercase mb-3"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              ¿Cómo deseas usar KD9?
            </div>
            <div className="space-y-2">
              {ROLES.map((r) => (
                <label
                  key={r.id}
                  className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    roleSel === r.id ? "kd9-glow" : ""
                  }`}
                  style={{
                    borderColor:
                      roleSel === r.id ? "rgba(212,175,55,0.6)" : "var(--kd9-border)",
                    background:
                      roleSel === r.id
                        ? "rgba(212,175,55,0.06)"
                        : "var(--kd9-bg-2)",
                  }}
                  data-testid={`register-role-option-${r.id}`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.id}
                    checked={roleSel === r.id}
                    onChange={() => setRoleSel(r.id)}
                    className="mt-1 accent-[color:var(--kd9-gold)]"
                  />
                  <div>
                    <div className="font-mont text-sm font-semibold">{r.label}</div>
                    <div
                      className="font-cormorant text-base"
                      style={{ color: "var(--kd9-text-soft)" }}
                    >
                      {r.desc}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {consultantSelected && (
            <div className="kd9-card p-4" data-testid="register-consultant-photo">
              <div className="font-mont text-[11px] tracking-[0.2em] uppercase kd9-gold-text mb-2">Fotografía frontal oficial · obligatoria</div>
              <div className="flex items-center gap-4">
                {consultantPhotoPreview ? <img src={consultantPhotoPreview} alt="Vista previa frontal" className="w-24 h-24 rounded-xl object-cover" style={{ border: "1px solid var(--kd9-gold)" }} /> : <div className="w-24 h-24 rounded-xl flex items-center justify-center border" style={{ borderColor: "var(--kd9-border)" }}><ImagePlus size={28} /></div>}
                <label className="kd9-btn-outline px-4 py-3 rounded-lg text-[10px] cursor-pointer">
                  Seleccionar fotografía
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" required={consultantSelected} onChange={(e) => selectConsultantPhoto(e.target.files?.[0])} data-testid="register-consultant-photo-input" />
                </label>
              </div>
              <p className="font-cormorant mt-3" style={{ color: "var(--kd9-text-soft)" }}>Usa una imagen reciente, frontal, nítida y sin filtros. Solo será pública cuando Dirección la apruebe.</p>
            </div>
          )}

          <div className="space-y-2 pt-2 text-sm font-mont" style={{ color: "var(--kd9-text-soft)" }}>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                className="w-4 h-4 accent-[color:var(--kd9-gold)]"
                data-testid="register-terms-check"
              />
              Acepto{" "}
              <span className="underline" style={{ color: "var(--kd9-gold)" }}>
                términos y condiciones
              </span>
            </label>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={privacy}
                onChange={(e) => setPrivacy(e.target.checked)}
                className="w-4 h-4 accent-[color:var(--kd9-gold)]"
                data-testid="register-privacy-check"
              />
              Acepto{" "}
              <span className="underline" style={{ color: "var(--kd9-gold)" }}>
                política de privacidad
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="kd9-btn-gold w-full py-4 rounded-xl text-[12px] flex items-center justify-center gap-3 mt-4"
            style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}
            data-testid="register-submit-btn"
          >
            <User size={16} /> {loading ? "Creando..." : "Crear Cuenta"} <ArrowRight size={16} />
          </button>

          <div className="text-center text-sm font-mont mt-4" style={{ color: "var(--kd9-text-soft)" }}>
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="font-semibold" style={{ color: "var(--kd9-gold)" }} data-testid="register-login-link">
              Iniciar sesión
            </Link>
          </div>
        </form>
      </div>
    </AuthShell>
  );
};

export default Register;
